import {
  SearchApiModel,
  useAxios,
  useDeleteNulls,
} from "@sttedra_ui/framework";

import ReportsApiResponseModel from "./reports.api-response-model.js";
import ReportsApiRequestModel from "./reports.api-request-model.js";

// #region Globals
const { base } = useAxios();
const timeout = 5000;
// #endregion

// #region Public functions
async function reportsList(searchParams = {}, from = null) {
  try {
    const searchModel = new SearchApiModel(searchParams);

    useDeleteNulls(searchModel);
    let url = `/UserReport/list`;

    if (from && from.model == "users") {
      searchModel.idUser = from.id;
    } else if (from && from.model == "user-groups") {
      url = `/UserReport/${from.id}/forGroup`;
    }

    const { data: response } = await base.post(url, searchModel, {
      timeout,
    });

    const { data: originalData, pageIndex, totalResults } = response;
    const pageSize = response.pageSize === 100 ? searchModel.pageSize : response.pageSize;

    const data = originalData.map((u) => new ReportsApiResponseModel(u));

    return {
      data,
      pageSize,
      pageIndex,
      totalResults,
    };
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al buscar los registros");

    return err;
  }
}

async function userReportListAll() {
  try {
    const url = `/UserReport/listAll`;

    const response = await base.get(url, {
      timeout: 20000,
    });

    const data = response.data.map((u) => new ReportsApiResponseModel(u));

    return { response, data };
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al buscar los registros");

    return err;
  }
}

async function userReportListByFilter(query) {
  try {
    const url = `/UserReport/listByFilter/${query}`;

    const response = await base.get(url, {
      timeout: 20000,
    });

    const data = response.data.map((u) => new ReportsApiResponseModel(u));

    return { response, data };
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al buscar los registros");

    return err;
  }
}

async function reportsCreate(params = {}) {
  try {
    const reportModel = new ReportsApiRequestModel(params);

    useDeleteNulls(reportModel);

    const url = `/UserReport/report/addReport`;

    const response = await base.post(url, reportModel, {
      timeout: 20000,
    });

    return response;
  } catch (err) {
    return err;
  }
}

async function reportsUpdate(params = {}) {
  try {
    const reportModel = new ReportsApiRequestModel(params);

    useDeleteNulls(reportModel);

    const url = `/UserReport/user/updateReport`;

    const response = await base.put(url, reportModel, {
      timeout: 20000,
    });

    return response;
  } catch (err) {
    return err;
  }
}

async function reportsDelete(id) {
  try {
    const url = `/UserReport/report/deleteReport`;

    const response = await base.delete(
      url,
      {
        data: {
          idInforme: id,
        },
      },
      {
        timeout,
      }
    );

    return response;
  } catch (err) {
    return err;
  }
}

// Public report details
async function userReportDetails(reportAlias) {
  try {
    const url = `/UserReport/${reportAlias}/details`;

    // Make the request
    const response = await base.get(url, {
      timeout: 20000,
    });

    return response;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al obtener los detalles");
    return err;
  }
}

// Public report additional details
async function userReportAdditionalDetails(reportAlias) {
  try {
    const url = `/UserReport/${reportAlias}/additionalDetails`;

    // Make the request
    const response = await base.get(url, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener los detalles adicionales"
    );

    return err;
  }
}

// Public report user list
async function userReportDetailsUser(data) {
  try {
    const url = `/UserReport/detailsUser`;

    // Make the request
    const response = await base.post(url, data, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener los usuarios del informe"
    );

    return err;
  }
}

// Public report user access
async function userReportUserAccess(data) {
  try {
    const url = `/UserReport/userAccess`;

    // Make the request
    const response = await base.post(url, data, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener los accesos del informe"
    );

    return err;
  }
}

// Get the report file list
async function userReportFileList(params) {
  try {
    const url = `/UserReport/${params.id}/fileList`;

    // Make the request
    const response = await base.get(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener la lista de archivos"
    );

    return err;
  }
}

// Add user to report
async function userReportAddUser(params) {
  try {
    const url = `/UserReport/user/addUser`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    return err;
  }
}

// Update the user information who is accessing the report
async function userReportUpdateUser(params) {
  try {
    const url = `/UserReport/user/updateUser`;

    // Make the request
    const response = await base.put(url, params, { timeout });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al actualizar el registro"
    );

    return err;
  }
}

// Delete the user from the report
async function userReportDeleteUser(params) {
  try {
    const url = `/UserReport/user/deleteUser`;

    // Make the request
    const response = await base.delete(
      url,
      { data: params },
      {
        timeout,
      }
    );

    return response.data;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al borrar el registro");

    return err;
  }
}

// Add rol to report
async function userReportAddRole(params) {
  try {
    const url = `/UserReport/role/addRole`;

    const response = await base.post(url, params, { timeout });

    return response.data;
  } catch (err) {
    return err;
  }
}

// Update the role information of the report
async function userReportUpdateRole(params) {
  try {
    const url = `/UserReport/role/updateRole`;

    const response = await base.put(url, params, { timeout });

    return response.data;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al actualizar un rol");

    return err;
  }
}

// Delete the role from the report
async function userReportDeleteRole(idInforme, orden) {
  try {
    const url = `/UserReport/role/deleteRole`;

    const response = await base.delete(
      url,
      {
        data: {
          idInforme,
          orden,
        },
      },
      {
        timeout,
      }
    );

    return response;
  } catch (err) {
    return err;
  }
}

// Add a user access to the report
async function userReportAccessReport(params) {
  try {
    const url = `/UserReport/report/accessReport`;

    // Make the request
    await base.post(url, params, {
      timeout,
    });
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al añadir un acceso al informe"
    );
  }
}

// Get the reports from PowerBI service by query (GUID)
async function powerBiGetReports(query) {
  try {
    const url = `/PowerBi/GetReports/${query}`;

    const response = await base.get(url, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al obtener los informes");

    return err;
  }
}

async function getReportTableData(params) {
  try {
    const url = `/UserReport/ExecuteReport`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al obtener los datos");

    return err;
  }
}

// Add the report to the favorites
async function addFavorite(params) {
  try {
    const url = `/UserReport/AddFavorite`;

    const response = await base.post(url, params, {
      timeout,
    });

    return response.data;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al actualizar si el informe es favorito"
    );

    return err;
  }
}

// Delete the report from the favorites
async function deleteFavorite(params) {
  try {
    const url = `/UserReport/DeleteFavorite`;

    const response = await base.delete(
      url,
      {
        data: {
          ...params,
        },
      },
      {
        timeout,
      }
    );

    return response.data;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al borrar el favorito");

    return err;
  }
}

async function downloadExecuteReport(params) {
  try {
    const url = `/UserReport/DownloadExecuteReport`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
      responseType: "blob",
    });

    return response;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al obtener los datos");

    return err;
  }
}

// Add group to report
async function userReportAddGroup(params) {
  try {
    const url = `/UserReport/user/AddUserGroup`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Delete the group from the report
async function userReportDeleteGroup(params) {
  try {
    const url = `/UserReport/user/deleteUserGroup`;

    // Make the request
    const response = await base.delete(
      url,
      { data: params },
      {
        timeout,
      }
    );

    return response;
  } catch (err) {
    localStorage.setItem("errorToastMessage", "Error al borrar el registro");

    return err;
  }
}

// Get all the groups from a report with its users
async function appUserReportDetailsGroup(params = {}) {
  try {
    const url = `/UserReport/detailsGroup`;

    // Make the request
    const response = await base.post(url, params, {
      timeout: 20000,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Update the user group within the report
async function appUserReportUpdateUserGroup(params = {}) {
  try {
    const url = `/UserReport/user/updateUserGroup`;

    // Make the request
    const response = await base.put(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    return err;
  }
}

// Check if the report has roles
async function reportHasRoles(params) {
  try {
    const url = `/PowerBI/ReportHasRoles`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al obtener si el informe tiene roles"
    );

    return err;
  }
}

// Copy users and groups to a reports
async function copyUsersAndGroups(params) {
  try {
    const url = `/UserReport/CloneUsersAndGroupsReport`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al copiar usuarios y grupos al informe"
    );

    return err;
  }
}

// Search reports by text
async function searchReportsByText(params) {
  try {
    const url = `/UserReport/listMinimal?filter=${params}`;

    // Make the request
    const response = await base.post(url, params, {
      timeout,
    });

    return response;
  } catch (err) {
    localStorage.setItem(
      "errorToastMessage",
      "Error al buscar los informes"
    );

    return err;
  }
}


// #endregion

// #region Private functions
// #endregion

export {
  reportsList,
  reportsCreate,
  reportsUpdate,
  reportsDelete,
  userReportListAll,
  userReportListByFilter,
  userReportDetails,
  userReportAdditionalDetails,
  userReportDetailsUser,
  userReportUserAccess,
  userReportFileList,
  userReportAddUser,
  userReportUpdateUser,
  userReportDeleteUser,
  userReportAddRole,
  userReportUpdateRole,
  userReportDeleteRole,
  userReportAccessReport,
  powerBiGetReports,
  getReportTableData,
  addFavorite,
  deleteFavorite,
  downloadExecuteReport,
  userReportAddGroup,
  userReportDeleteGroup,
  appUserReportDetailsGroup,
  reportHasRoles,
  appUserReportUpdateUserGroup,
  copyUsersAndGroups,
  searchReportsByText
};
